import React, { ReactNode } from 'react';
import cl from 'classnames';
import { useLanguage } from 'src/hooks';
import { Button, Modal } from 'src/components/general';
import styles from './filterModalStyle.module.scss';

type FilterModalProps = {
  show: boolean;
  close: () => void;
  onApply: () => void;
  onCancel: () => void;
  isSearch?: boolean;
  header?: string;
  children: ReactNode;
};

export const FilterModal = ({
  show,
  close,
  onApply,
  onCancel,
  isSearch = false,
  header,
  children,
}: FilterModalProps) => {
  const { t, td } = useLanguage();

  return (
    <Modal
      header={td(header || (isSearch ? 'general.search' : 'general.filters'))}
      className={styles.modal}
      show={show}
      close={close}
      footer={
        <>
          <Button
            label={t(isSearch ? 'general.find' : 'general.apply')}
            onClick={onApply}
            design="lightBlue"
            size="small"
            className={styles.modalActionButton}
          />
          <Button
            label={t(isSearch ? 'general.cancel' : 'general.reset')}
            onClick={onCancel}
            design="outlineBlue"
            size="small"
            className={styles.modalCancelButton}
          />
        </>
      }
    >
      <div className={styles.modalContent}>{children}</div>
    </Modal>
  );
};

export const FilterModalButton: React.FC<{
  setShow?: (value: boolean) => void;
  icon?:
    | 'filter'
    | 'search'
    | 'print'
    | 'download'
    | 'sort'
    | 'exportToXlsx'
    | 'exportToPdf';
  className?: string;
  disabled?: boolean | null;
  badge?: boolean;
}> = ({
  icon = 'filter',
  setShow,
  className,
  disabled = false,
  badge = false,
}) => {
  return (
    <div
      className={cl(
        styles.filterButton,
        disabled && styles.disabled,
        className,
      )}
      onClick={() => {
        if (disabled) {
          return;
        }

        setShow?.(true);
      }}
    >
      {badge && <div className={styles.badge} />}
      {icon === 'filter' && (
        <svg
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.4438 3.44458H2.66602L9.77713 11.8535V17.6668L13.3327 19.4446V11.8535L20.4438 3.44458Z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'search' && (
        <svg
          width="23"
          height="22"
          viewBox="0 0 23 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.4443 18.9999L14.1111 13.6665M15.8888 9.2221C15.8888 12.6585 13.103 15.4443 9.66656 15.4443C6.23012 15.4443 3.44434 12.6585 3.44434 9.2221C3.44434 5.78566 6.23012 2.99988 9.66656 2.99988C13.103 2.99988 15.8888 5.78566 15.8888 9.2221Z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'print' && (
        <svg
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.19982 17.8002H3.09991C2.54298 17.8002 2.00886 17.5789 1.61505 17.1851C1.22124 16.7913 1 16.2572 1 15.7003V10.4505C1 9.89356 1.22124 9.35944 1.61505 8.96564C2.00886 8.57183 2.54298 8.35059 3.09991 8.35059H19.8992C20.4561 8.35059 20.9902 8.57183 21.384 8.96564C21.7778 9.35944 21.9991 9.89356 21.9991 10.4505V15.7003C21.9991 16.2572 21.7778 16.7913 21.384 17.1851C20.9902 17.5789 20.4561 17.8002 19.8992 17.8002H17.7993"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.8006 13.6006H5.20117V22.0002H17.8006V13.6006Z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.20117 8.34968V1H17.8006V8.34968"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'download' && (
        <svg
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.125 8.75L10.5 13.125L14.875 8.75"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.5 13.125L10.5 2.625"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.375 13.125V16.625C18.375 17.0891 18.1906 17.5342 17.8624 17.8624C17.5342 18.1906 17.0891 18.375 16.625 18.375H4.375C3.91087 18.375 3.46575 18.1906 3.13756 17.8624C2.80937 17.5342 2.625 17.0891 2.625 16.625V13.125"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'sort' && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.79289 9.7071C2.40237 9.31657 2.40237 8.68341 2.79289 8.29288L8.79289 2.29288C9.07889 2.00689 9.50901 1.92133 9.88268 2.07611C10.2564 2.23089 10.5 2.59553 10.5 2.99999L10.5 21C10.5 21.5523 10.0523 22 9.5 22C8.94772 22 8.5 21.5523 8.5 21V5.4142L4.20711 9.7071C3.81658 10.0976 3.18342 10.0976 2.79289 9.7071ZM14.1173 21.5244C13.7436 21.3696 13.5 21.005 13.5 20.6005L13.5 2.60049C13.5 2.04821 13.9477 1.60049 14.5 1.60049C15.0523 1.60049 15.5 2.04821 15.5 2.60049L15.5 18.1863L19.7929 13.8934C20.1834 13.5029 20.8166 13.5029 21.2071 13.8934C21.5976 14.2839 21.5976 14.9171 21.2071 15.3076L15.2071 21.3076C14.9211 21.5936 14.491 21.6792 14.1173 21.5244Z"
            fill="white"
          />
        </svg>
      )}
      {icon === 'exportToXlsx' && (
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_1_155)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.25 2.18254V22.8175C6.25 24.0228 7.21189 25 8.39844 25H22.8516C24.0381 25 25 24.0228 25 22.8175V5.74318C25 5.16017 24.7704 4.6014 24.3624 4.19143L20.8186 0.630791C20.4164 0.226664 19.8735 0 19.3078 0H8.39844C7.21189 0 6.25 0.977156 6.25 2.18254ZM7.61719 22.8175V2.18254C7.61719 1.74422 7.96697 1.38889 8.39844 1.38889H19.043V6.15079C19.043 6.97265 19.6988 7.63889 20.5078 7.63889H23.6328V22.8175C23.6328 23.2558 23.283 23.6111 22.8516 23.6111H8.39844C7.96697 23.6111 7.61719 23.2558 7.61719 22.8175ZM23.6328 6.25V5.74318C23.6328 5.53118 23.5493 5.32799 23.4009 5.1789L20.4102 2.17386V6.15079C20.4102 6.20558 20.4539 6.25 20.5078 6.25H23.6328Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.0156 10.2539C16.0156 9.87637 16.3217 9.57031 16.6992 9.57031H21.1914C21.5689 9.57031 21.875 9.87637 21.875 10.2539C21.875 10.6314 21.5689 10.9375 21.1914 10.9375H16.6992C16.3217 10.9375 16.0156 10.6314 16.0156 10.2539Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.0156 13.3789C16.0156 13.0014 16.3217 12.6953 16.6992 12.6953H21.1914C21.5689 12.6953 21.875 13.0014 21.875 13.3789C21.875 13.7564 21.5689 14.0625 21.1914 14.0625H16.6992C16.3217 14.0625 16.0156 13.7564 16.0156 13.3789Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.0156 16.5039C16.0156 16.1264 16.3217 15.8203 16.6992 15.8203H21.1914C21.5689 15.8203 21.875 16.1264 21.875 16.5039C21.875 16.8814 21.5689 17.1875 21.1914 17.1875H16.6992C16.3217 17.1875 16.0156 16.8814 16.0156 16.5039Z"
              fill="white"
            />
            <path
              d="M0 7.22656C0 6.57935 0.524666 6.05469 1.17188 6.05469H12.8906C13.5378 6.05469 14.0625 6.57935 14.0625 7.22656V18.9453C14.0625 19.5925 13.5378 20.1172 12.8906 20.1172H1.17187C0.524666 20.1172 0 19.5925 0 18.9453V7.22656Z"
              fill="white"
            />
            <path
              d="M3.90625 17.009L6.10123 12.9234L4.10919 9.17969H5.62592L6.91299 11.6951L8.17337 9.17969H9.67407L7.68204 12.9822L9.87701 17.009H8.31223L6.88629 14.3013L5.46036 17.009H3.90625Z"
              fill="#DD7BC1"
            />
          </g>
          <defs>
            <clipPath id="clip0_1_155">
              <rect width="25" height="25" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
      {icon === 'exportToPdf' && (
        <svg
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.69699 11.785C6.69699 11.1656 6.26693 10.796 5.50799 10.796C5.1984 10.796 4.98865 10.8258 4.87866 10.8558V12.8446C5.00869 12.8741 5.16854 12.8842 5.38777 12.8842C6.19749 12.8842 6.69699 12.4746 6.69699 11.785Z"
            fill="#FFFFFF"
          />
          <path
            d="M11.3944 10.816C11.0543 10.816 10.835 10.8459 10.7046 10.8759V15.2822C10.8347 15.3127 11.0446 15.3127 11.2345 15.3127C12.6132 15.3223 13.5125 14.5633 13.5125 12.9545C13.5226 11.5551 12.7031 10.816 11.3944 10.816Z"
            fill="#FFFFFF"
          />
          <path
            d="M20.3953 8.22699H19.843V5.56221C19.843 5.54559 19.8404 5.52884 19.8382 5.51201C19.8373 5.4062 19.8036 5.30227 19.7312 5.21992L15.2967 0.154252C15.2953 0.152957 15.294 0.152498 15.2931 0.151078C15.2667 0.121556 15.2359 0.0970024 15.2033 0.0757479C15.1937 0.0692755 15.1839 0.063847 15.1738 0.0582515C15.1456 0.0428431 15.1157 0.0300653 15.0848 0.0208369C15.0765 0.0184985 15.069 0.0150744 15.0606 0.012903C15.0271 0.00496913 14.9923 0 14.9571 0H4.05872C3.56106 0 3.15676 0.404754 3.15676 0.902001V8.22679H2.6046C1.89268 8.22679 1.31543 8.80379 1.31543 9.51596V16.2199C1.31543 16.9316 1.89268 17.5089 2.6046 17.5089H3.1568V22.098C3.1568 22.5952 3.5611 23 4.05876 23H18.9411C19.4383 23 19.843 22.5952 19.843 22.098V17.5089H20.3953C21.107 17.5089 21.6844 16.9316 21.6844 16.2199V9.51617C21.6844 8.80404 21.107 8.22699 20.3953 8.22699ZM4.05872 0.902168H14.5061V5.5169C14.5061 5.76607 14.7083 5.96788 14.9571 5.96788H18.941V8.22729H4.05872V0.902168ZM15.1311 12.904C15.1311 14.1933 14.6616 15.0823 14.0111 15.6321C13.3025 16.2216 12.2231 16.5013 10.9045 16.5013C10.1153 16.5013 9.55591 16.4513 9.17575 16.4015V9.78647C9.73526 9.69673 10.4651 9.64675 11.2346 9.64675C12.5128 9.64675 13.3424 9.87674 13.9922 10.3661C14.6913 10.8857 15.1311 11.7149 15.1311 12.904ZM3.36972 16.4315V9.78647C3.83912 9.70696 4.49893 9.64675 5.4282 9.64675C6.36736 9.64675 7.03698 9.82664 7.48646 10.1866C7.91623 10.5261 8.20531 11.0858 8.20531 11.7453C8.20531 12.405 7.98609 12.9645 7.58609 13.344C7.06655 13.8337 6.29725 14.0537 5.39771 14.0537C5.1982 14.0537 5.01772 14.0435 4.87846 14.0235V16.4316H3.36972V16.4315ZM18.9411 21.8538H4.05872V17.5089H18.941V21.8538H18.9411ZM20.248 10.9457H17.6595V12.4845H20.0778V13.7236H17.6595V16.4315H16.1314V9.69673H20.248V10.9457Z"
            fill="#FFFFFF"
          />
        </svg>
      )}
    </div>
  );
};

export const FilterModalIndicator: React.FC<{
  icon?:
    | 'filter'
    | 'search'
    | 'calendar'
    | 'print'
    | 'download'
    | 'sort'
    | 'exportToXlsx'
    | 'exportToPdf';
  className?: string;
  disabled?: boolean | null;
  text?: string | ReactNode;
  clear?: () => void;
}> = ({ icon = 'filter', className, disabled = false, text, clear }) => {
  if (!text) {
    return null;
  }

  return (
    <div
      className={cl(
        styles.filterIndicator,
        disabled && styles.disabled,
        className,
      )}
    >
      {icon === 'filter' && (
        <svg
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.4438 3.44458H2.66602L9.77713 11.8535V17.6668L13.3327 19.4446V11.8535L20.4438 3.44458Z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'search' && (
        <svg
          width="23"
          height="22"
          viewBox="0 0 23 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.4443 18.9999L14.1111 13.6665M15.8888 9.2221C15.8888 12.6585 13.103 15.4443 9.66656 15.4443C6.23012 15.4443 3.44434 12.6585 3.44434 9.2221C3.44434 5.78566 6.23012 2.99988 9.66656 2.99988C13.103 2.99988 15.8888 5.78566 15.8888 9.2221Z"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'calendar' && (
        <svg
          width="24"
          height="25"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.93319 2.5C8.40245 2.5 8.78286 2.88041 8.78286 3.34967V4.53341H15.2171V3.34967C15.2171 2.88041 15.5976 2.5 16.0668 2.5C16.5361 2.5 16.9165 2.88041 16.9165 3.34967V4.53341H19.1169C19.8816 4.53341 20.6149 4.83716 21.1556 5.37784C21.6962 5.91852 22 6.65184 22 7.41649V19.6169C22 20.3816 21.6962 21.1149 21.1556 21.6556C20.6149 22.1962 19.8816 22.5 19.1169 22.5H4.88308C4.11844 22.5 3.38512 22.1962 2.84443 21.6556C2.30375 21.1149 2 20.3816 2 19.6169V7.41649C2 6.65185 2.30375 5.91852 2.84443 5.37784C3.38512 4.83716 4.11844 4.53341 4.88308 4.53341H7.08352V3.34967C7.08352 2.88041 7.46393 2.5 7.93319 2.5ZM7.08352 6.23275H4.88308C4.56913 6.23275 4.26805 6.35747 4.04605 6.57946C3.82406 6.80145 3.69935 7.10254 3.69935 7.41649V19.6169C3.69935 19.9309 3.82406 20.232 4.04605 20.4539C4.26805 20.6759 4.56913 20.8007 4.88308 20.8007H19.1169C19.4309 20.8007 19.732 20.6759 19.9539 20.4539C20.1759 20.232 20.3007 19.9309 20.3007 19.6169V7.41649C20.3007 7.10254 20.1759 6.80145 19.9539 6.57946C19.732 6.35747 19.4309 6.23275 19.1169 6.23275H16.9165V7.41649C16.9165 7.88575 16.5361 8.26616 16.0668 8.26616C15.5976 8.26616 15.2171 7.88575 15.2171 7.41649V6.23275H8.78286V7.41649C8.78286 7.88575 8.40245 8.26616 7.93319 8.26616C7.46393 8.26616 7.08352 7.88575 7.08352 7.41649V6.23275ZM6.06681 11.4833C6.06681 11.014 6.44722 10.6336 6.91649 10.6336H17.0835C17.5528 10.6336 17.9332 11.014 17.9332 11.4833C17.9332 11.9526 17.5528 12.333 17.0835 12.333H6.91649C6.44722 12.333 6.06681 11.9526 6.06681 11.4833Z"
            fill="white"
          />
        </svg>
      )}
      {icon === 'print' && (
        <svg
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.19982 17.8002H3.09991C2.54298 17.8002 2.00886 17.5789 1.61505 17.1851C1.22124 16.7913 1 16.2572 1 15.7003V10.4505C1 9.89356 1.22124 9.35944 1.61505 8.96564C2.00886 8.57183 2.54298 8.35059 3.09991 8.35059H19.8992C20.4561 8.35059 20.9902 8.57183 21.384 8.96564C21.7778 9.35944 21.9991 9.89356 21.9991 10.4505V15.7003C21.9991 16.2572 21.7778 16.7913 21.384 17.1851C20.9902 17.5789 20.4561 17.8002 19.8992 17.8002H17.7993"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M17.8006 13.6006H5.20117V22.0002H17.8006V13.6006Z"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.20117 8.34968V1H17.8006V8.34968"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'download' && (
        <svg
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.125 8.75L10.5 13.125L14.875 8.75"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.5 13.125L10.5 2.625"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18.375 13.125V16.625C18.375 17.0891 18.1906 17.5342 17.8624 17.8624C17.5342 18.1906 17.0891 18.375 16.625 18.375H4.375C3.91087 18.375 3.46575 18.1906 3.13756 17.8624C2.80937 17.5342 2.625 17.0891 2.625 16.625V13.125"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {icon === 'sort' && (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.79289 9.7071C2.40237 9.31657 2.40237 8.68341 2.79289 8.29288L8.79289 2.29288C9.07889 2.00689 9.50901 1.92133 9.88268 2.07611C10.2564 2.23089 10.5 2.59553 10.5 2.99999L10.5 21C10.5 21.5523 10.0523 22 9.5 22C8.94772 22 8.5 21.5523 8.5 21V5.4142L4.20711 9.7071C3.81658 10.0976 3.18342 10.0976 2.79289 9.7071ZM14.1173 21.5244C13.7436 21.3696 13.5 21.005 13.5 20.6005L13.5 2.60049C13.5 2.04821 13.9477 1.60049 14.5 1.60049C15.0523 1.60049 15.5 2.04821 15.5 2.60049L15.5 18.1863L19.7929 13.8934C20.1834 13.5029 20.8166 13.5029 21.2071 13.8934C21.5976 14.2839 21.5976 14.9171 21.2071 15.3076L15.2071 21.3076C14.9211 21.5936 14.491 21.6792 14.1173 21.5244Z"
            fill="white"
          />
        </svg>
      )}
      {icon === 'exportToXlsx' && (
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_1_155)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.25 2.18254V22.8175C6.25 24.0228 7.21189 25 8.39844 25H22.8516C24.0381 25 25 24.0228 25 22.8175V5.74318C25 5.16017 24.7704 4.6014 24.3624 4.19143L20.8186 0.630791C20.4164 0.226664 19.8735 0 19.3078 0H8.39844C7.21189 0 6.25 0.977156 6.25 2.18254ZM7.61719 22.8175V2.18254C7.61719 1.74422 7.96697 1.38889 8.39844 1.38889H19.043V6.15079C19.043 6.97265 19.6988 7.63889 20.5078 7.63889H23.6328V22.8175C23.6328 23.2558 23.283 23.6111 22.8516 23.6111H8.39844C7.96697 23.6111 7.61719 23.2558 7.61719 22.8175ZM23.6328 6.25V5.74318C23.6328 5.53118 23.5493 5.32799 23.4009 5.1789L20.4102 2.17386V6.15079C20.4102 6.20558 20.4539 6.25 20.5078 6.25H23.6328Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.0156 10.2539C16.0156 9.87637 16.3217 9.57031 16.6992 9.57031H21.1914C21.5689 9.57031 21.875 9.87637 21.875 10.2539C21.875 10.6314 21.5689 10.9375 21.1914 10.9375H16.6992C16.3217 10.9375 16.0156 10.6314 16.0156 10.2539Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.0156 13.3789C16.0156 13.0014 16.3217 12.6953 16.6992 12.6953H21.1914C21.5689 12.6953 21.875 13.0014 21.875 13.3789C21.875 13.7564 21.5689 14.0625 21.1914 14.0625H16.6992C16.3217 14.0625 16.0156 13.7564 16.0156 13.3789Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.0156 16.5039C16.0156 16.1264 16.3217 15.8203 16.6992 15.8203H21.1914C21.5689 15.8203 21.875 16.1264 21.875 16.5039C21.875 16.8814 21.5689 17.1875 21.1914 17.1875H16.6992C16.3217 17.1875 16.0156 16.8814 16.0156 16.5039Z"
              fill="white"
            />
            <path
              d="M0 7.22656C0 6.57935 0.524666 6.05469 1.17188 6.05469H12.8906C13.5378 6.05469 14.0625 6.57935 14.0625 7.22656V18.9453C14.0625 19.5925 13.5378 20.1172 12.8906 20.1172H1.17187C0.524666 20.1172 0 19.5925 0 18.9453V7.22656Z"
              fill="white"
            />
            <path
              d="M3.90625 17.009L6.10123 12.9234L4.10919 9.17969H5.62592L6.91299 11.6951L8.17337 9.17969H9.67407L7.68204 12.9822L9.87701 17.009H8.31223L6.88629 14.3013L5.46036 17.009H3.90625Z"
              fill="#DD7BC1"
            />
          </g>
          <defs>
            <clipPath id="clip0_1_155">
              <rect width="25" height="25" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
      {icon === 'exportToPdf' && (
        <svg
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.69699 11.785C6.69699 11.1656 6.26693 10.796 5.50799 10.796C5.1984 10.796 4.98865 10.8258 4.87866 10.8558V12.8446C5.00869 12.8741 5.16854 12.8842 5.38777 12.8842C6.19749 12.8842 6.69699 12.4746 6.69699 11.785Z"
            fill="#FFFFFF"
          />
          <path
            d="M11.3944 10.816C11.0543 10.816 10.835 10.8459 10.7046 10.8759V15.2822C10.8347 15.3127 11.0446 15.3127 11.2345 15.3127C12.6132 15.3223 13.5125 14.5633 13.5125 12.9545C13.5226 11.5551 12.7031 10.816 11.3944 10.816Z"
            fill="#FFFFFF"
          />
          <path
            d="M20.3953 8.22699H19.843V5.56221C19.843 5.54559 19.8404 5.52884 19.8382 5.51201C19.8373 5.4062 19.8036 5.30227 19.7312 5.21992L15.2967 0.154252C15.2953 0.152957 15.294 0.152498 15.2931 0.151078C15.2667 0.121556 15.2359 0.0970024 15.2033 0.0757479C15.1937 0.0692755 15.1839 0.063847 15.1738 0.0582515C15.1456 0.0428431 15.1157 0.0300653 15.0848 0.0208369C15.0765 0.0184985 15.069 0.0150744 15.0606 0.012903C15.0271 0.00496913 14.9923 0 14.9571 0H4.05872C3.56106 0 3.15676 0.404754 3.15676 0.902001V8.22679H2.6046C1.89268 8.22679 1.31543 8.80379 1.31543 9.51596V16.2199C1.31543 16.9316 1.89268 17.5089 2.6046 17.5089H3.1568V22.098C3.1568 22.5952 3.5611 23 4.05876 23H18.9411C19.4383 23 19.843 22.5952 19.843 22.098V17.5089H20.3953C21.107 17.5089 21.6844 16.9316 21.6844 16.2199V9.51617C21.6844 8.80404 21.107 8.22699 20.3953 8.22699ZM4.05872 0.902168H14.5061V5.5169C14.5061 5.76607 14.7083 5.96788 14.9571 5.96788H18.941V8.22729H4.05872V0.902168ZM15.1311 12.904C15.1311 14.1933 14.6616 15.0823 14.0111 15.6321C13.3025 16.2216 12.2231 16.5013 10.9045 16.5013C10.1153 16.5013 9.55591 16.4513 9.17575 16.4015V9.78647C9.73526 9.69673 10.4651 9.64675 11.2346 9.64675C12.5128 9.64675 13.3424 9.87674 13.9922 10.3661C14.6913 10.8857 15.1311 11.7149 15.1311 12.904ZM3.36972 16.4315V9.78647C3.83912 9.70696 4.49893 9.64675 5.4282 9.64675C6.36736 9.64675 7.03698 9.82664 7.48646 10.1866C7.91623 10.5261 8.20531 11.0858 8.20531 11.7453C8.20531 12.405 7.98609 12.9645 7.58609 13.344C7.06655 13.8337 6.29725 14.0537 5.39771 14.0537C5.1982 14.0537 5.01772 14.0435 4.87846 14.0235V16.4316H3.36972V16.4315ZM18.9411 21.8538H4.05872V17.5089H18.941V21.8538H18.9411ZM20.248 10.9457H17.6595V12.4845H20.0778V13.7236H17.6595V16.4315H16.1314V9.69673H20.248V10.9457Z"
            fill="#FFFFFF"
          />
        </svg>
      )}
      <div className={styles.text}>{text}</div>
      {clear && (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.clear}
          onClick={() => {
            if (disabled) {
              return;
            }

            clear();
          }}
        >
          <g clipPath="url(#clip0_38_2764)">
            <path
              d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_38_2764">
              <rect width="18" height="18" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
    </div>
  );
};

export default FilterModal;
